import { createFileRoute, Navigate, redirect, useRouterState } from '@tanstack/react-router';
import { z } from 'zod';

import { useAbility } from '@features/authorization';
import {
  normalizeCrmMembershipDetailReturnTo,
  normalizeMembershipDetailSearch,
  projectMembershipRegisterSearch,
} from '@features/crm-customer-membership';
import { CrmMembershipRecordPage } from '@pages/crm';
import type { RouterContext } from '@shared/config/router-context';
import {
  canAccessCrmCustomerDocuments,
  canAccessCrmCustomerMembershipDirectory,
  canAccessCrmCustomerProfile,
} from '@shared/lib';

export const Route = createFileRoute('/$locale/_authenticated/crm/memberships/$membershipId')({
  params: z.object({ membershipId: z.uuid() }),
  validateSearch: normalizeMembershipDetailSearch,
  beforeLoad: ({ context, params }) => {
    const { auth, ability } = context as RouterContext;
    if (auth.isLoading || !auth.isAuthenticated) return;
    if (!canAccessCrmCustomerMembershipDirectory(ability)) {
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect({
        to: '/$locale/unauthorized',
        params: { locale: params.locale },
      });
    }
  },
  component: CrmMembershipRecordRoute,
});

function CrmMembershipRecordRoute() {
  const { locale, membershipId } = Route.useParams();
  const ability = useAbility();
  const navigate = Route.useNavigate();
  const search = Route.useSearch();
  const currentLocation = useRouterState({ select: (state) => state.location.href });
  const canAccessMemberships = canAccessCrmCustomerMembershipDirectory(ability);
  const membershipReturnTo = normalizeCrmMembershipDetailReturnTo(currentLocation);
  const activeTab = search.membershipTab === 'documents' ? 'documents' : 'general';

  if (!canAccessMemberships) {
    return <Navigate to="/$locale/unauthorized" params={{ locale }} replace />;
  }

  return (
    <main className="min-w-0 p-4 sm:p-6">
      <CrmMembershipRecordPage
        activeTab={activeTab}
        startInEditMode={search.edit === '1'}
        onEditEntryConsumed={() => {
          void navigate({
            replace: true,
            resetScroll: false,
            search: (previous) => ({ ...previous, edit: undefined }),
          });
        }}
        membershipId={membershipId}
        onBack={() => {
          if (search.returnTo !== undefined) {
            void navigate({ to: search.returnTo as never });
            return;
          }
          void navigate({
            to: '/$locale/crm/memberships',
            params: { locale },
            search: projectMembershipRegisterSearch(search),
          });
        }}
        onOpenOwner={
          canAccessCrmCustomerProfile(ability)
            ? (customerId) => {
                void navigate({
                  to: '/$locale/crm/user-profiles/$userProfileId',
                  params: { userProfileId: customerId, locale },
                  search: { membershipReturnTo },
                });
              }
            : undefined
        }
        onOpenDocument={
          canAccessCrmCustomerDocuments(ability)
            ? (customerId, documentId, tab) => {
                void navigate({
                  to: '/$locale/crm/user-profiles/$userProfileId/documents/$documentId',
                  params: { userProfileId: customerId, documentId, locale },
                  search: {
                    documentTab: tab === 'file' ? 'file' : undefined,
                    membershipReturnTo,
                  },
                });
              }
            : undefined
        }
        onOpenSubmittingInstructor={
          canAccessCrmCustomerProfile(ability)
            ? (customerId) => {
                void navigate({
                  to: '/$locale/crm/user-profiles/$userProfileId',
                  params: { userProfileId: customerId, locale },
                  search: { membershipReturnTo },
                });
              }
            : undefined
        }
        onTabChange={(tab) => {
          void navigate({
            replace: true,
            resetScroll: false,
            search: { ...search, membershipTab: tab === 'documents' ? 'documents' : undefined },
          });
        }}
      />
    </main>
  );
}
